import {
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { cn } from "@/lib/cn";
import { Text } from "@/components/typography";
import { Button } from "./Button";
import styles from "./ui.module.css";
import {
  ToastContext,
  type ToastItem,
  type ToastTone,
} from "@/app/providers/toast-context";

export type { ToastItem, ToastTone };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const push = useCallback(
    (toast: Omit<ToastItem, "id"> & { id?: string }) => {
      const id = toast.id ?? crypto.randomUUID();
      setItems((current) => [
        ...current,
        {
          id,
          title: toast.title,
          ...(toast.description !== undefined
            ? { description: toast.description }
            : {}),
          tone: toast.tone ?? "info",
        },
      ]);
      window.setTimeout(() => dismiss(id), 5000);
      return id;
    },
    [dismiss],
  );

  const value = useMemo(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={styles.toastViewport} aria-live="polite">
        {items.map((item) => (
          <div key={item.id} className={cn(styles.toast)} role="status">
            <Text role="bodyStrong" as="div">
              {item.title}
            </Text>
            {item.description ? (
              <Text role="small" color="secondary" as="div">
                {item.description}
              </Text>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => dismiss(item.id)}
              aria-label="Dismiss notification"
            >
              Dismiss
            </Button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
